import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Sparkles,
  UserCircle,
  Camera,
  Radio,
  PhoneCall,
  Wallet,
  CreditCard,
  Crown,
  ShieldCheck,
  ArrowLeft,
  ChevronRight,
  HelpCircle,
  X,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import BackButton from '../components/BackButton';
import { WhatsAppLogo } from '../components/WhatsAppWidget';
import api from '../api/client';

const CATEGORY_ICONS = {
  start: Sparkles,
  profile: UserCircle,
  media: Camera,
  lives: Radio,
  o2o: PhoneCall,
  credits: Wallet,
  payments: CreditCard,
  creator: Crown,
  safety: ShieldCheck,
};

/** Render article body: plain paragraphs + "• " bullet lists. */
const ArticleBody = ({ body }) => {
  const blocks = (body || '').split('\n\n');
  return (
    <div className="space-y-3 text-[13.5px] leading-relaxed text-muted-foreground">
      {blocks.map((block, bi) => {
        const lines = block.split('\n');
        const isList = lines.every((l) => l.trim().startsWith('• ') || l.trim() === '');
        if (isList) {
          return (
            <ul key={bi} className="space-y-1.5 pl-1">
              {lines.filter(Boolean).map((l, li) => (
                <li key={li} className="flex gap-2">
                  <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                  <span>{l.trim().replace(/^• /, '')}</span>
                </li>
              ))}
            </ul>
          );
        }
        return lines.map((l, li) => (
          l.trim().startsWith('• ') ? (
            <p key={`${bi}-${li}`} className="flex gap-2">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
              <span>{l.trim().replace(/^• /, '')}</span>
            </p>
          ) : (
            <p key={`${bi}-${li}`} className={/[:]$/.test(l.trim()) ? 'font-semibold text-foreground' : ''}>{l}</p>
          )
        ));
      })}
    </div>
  );
};

const ContactCta = ({ t, waEnabled }) => (
  <div className="mt-10 rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-6 text-center" data-testid="help-contact-cta">
    <HelpCircle className="mx-auto h-7 w-7 gold-text" strokeWidth={1.6} />
    <h3 className="mt-3 text-base font-semibold">{t('docs.page.contact_title')}</h3>
    <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{t('docs.page.contact_text')}</p>
    <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
      <Link to="/contact">
        <Button className="gold-gradient rounded-full px-7" data-testid="help-contact-btn">
          {t('docs.page.contact_cta')}
        </Button>
      </Link>
      {waEnabled && (
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event('nymia:open-support-chat'))}
          className="flex items-center justify-center gap-2 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 px-6 py-2.5 text-sm font-semibold text-[#25D366] transition-colors hover:bg-[#25D366]/20"
          data-testid="help-whatsapp-btn"
        >
          <WhatsAppLogo className="h-4 w-4" />
          {t('docs.page.whatsapp_cta')}
        </button>
      )}
    </div>
  </div>
);

export default function HelpCenterPage() {
  const { t } = useTranslation('helpCenter');
  const [query, setQuery] = useState('');
  const [selectedCat, setSelectedCat] = useState(null);
  const [openArticle, setOpenArticle] = useState('');
  const [waEnabled, setWaEnabled] = useState(false);

  useEffect(() => {
    api.get('/whatsapp-widget/config')
      .then(({ data }) => setWaEnabled(!!(data && data.enabled && data.phone)))
      .catch(() => setWaEnabled(false));
  }, []);

  const categories = t('docs.categories', { returnObjects: true }) || [];

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return null;
    const words = q.split(/\s+/).filter(Boolean);
    const out = [];
    for (const cat of categories) {
      for (const art of cat.articles || []) {
        const hay = `${art.title} ${art.body}`.toLowerCase();
        if (words.every((w) => hay.includes(w))) {
          out.push({ cat, art, inTitle: words.some((w) => art.title.toLowerCase().includes(w)) });
        }
      }
    }
    out.sort((a, b) => Number(b.inTitle) - Number(a.inTitle));
    return out;
  }, [query, categories]);

  const openResult = (catId, artId) => {
    setQuery('');
    setSelectedCat(catId);
    setOpenArticle(artId);
  };

  const category = selectedCat ? categories.find((c) => c.id === selectedCat) : null;

  return (
    <div className="mx-auto max-w-3xl px-4 pb-24 pt-6 md:pt-10" data-testid="help-center-page">
      <BackButton className="mb-4" />

      {/* Hero + search */}
      <div className="text-center">
        <h1 className="text-3xl font-semibold sm:text-4xl" data-testid="help-title">{t('docs.page.title')}</h1>
        <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">{t('docs.page.subtitle')}</p>
        <div className="relative mx-auto mt-6 max-w-xl">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('docs.page.search_placeholder')}
            className="h-12 rounded-full border-border bg-card pl-11 pr-10 text-sm"
            data-testid="help-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
              data-testid="help-search-clear"
              aria-label="clear"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Search results */}
      {results !== null ? (
        <div className="mt-8" data-testid="help-search-results">
          {results.length === 0 ? (
            <div className="py-10 text-center" data-testid="help-no-results">
              <p className="text-sm font-semibold">{t('docs.page.no_results')} “{query}”</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('docs.page.no_results_hint')}</p>
            </div>
          ) : (
            <>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {results.length} {t('docs.page.results_count')}
              </p>
              <div className="space-y-2">
                {results.map(({ cat, art }) => (
                  <button
                    key={`${cat.id}-${art.id}`}
                    onClick={() => openResult(cat.id, art.id)}
                    className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-amber-500/40"
                    data-testid={`help-result-${cat.id}-${art.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider gold-text">{cat.title}</span>
                      <p className="truncate text-sm font-semibold">{art.title}</p>
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{art.body.split('\n')[0]}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </>
          )}
          <ContactCta t={t} waEnabled={waEnabled} />
        </div>
      ) : category ? (
        /* Category detail */
        <div className="mt-8" data-testid={`help-category-detail-${category.id}`}>
          <button
            onClick={() => { setSelectedCat(null); setOpenArticle(''); }}
            className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
            data-testid="help-back-to-categories"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t('docs.page.all_categories')}
          </button>
          <div className="mb-4 flex items-center gap-3">
            {(() => { const Icon = CATEGORY_ICONS[category.id] || HelpCircle; return (
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-500/30 gold-text">
                <Icon className="h-5 w-5" strokeWidth={1.7} />
              </span>
            ); })()}
            <div>
              <h2 className="text-xl font-semibold">{category.title}</h2>
              <p className="text-xs text-muted-foreground">{category.desc}</p>
            </div>
          </div>
          <Accordion
            type="single"
            collapsible
            value={openArticle}
            onValueChange={setOpenArticle}
            className="rounded-xl border border-border bg-card px-4"
          >
            {(category.articles || []).map((art) => (
              <AccordionItem key={art.id} value={art.id} data-testid={`help-article-${category.id}-${art.id}`}>
                <AccordionTrigger className="text-left text-sm font-semibold hover:no-underline">
                  {art.title}
                </AccordionTrigger>
                <AccordionContent>
                  <ArticleBody body={art.body} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <ContactCta t={t} waEnabled={waEnabled} />
        </div>
      ) : (
        /* Categories grid */
        <div className="mt-8">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="help-categories-grid">
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat.id] || HelpCircle;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setSelectedCat(cat.id); setOpenArticle(''); }}
                  className="group flex items-start gap-3.5 rounded-xl border border-border bg-card p-5 text-left transition-colors hover:border-amber-500/40"
                  data-testid={`help-category-${cat.id}`}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-amber-500/30 gold-text">
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold">{cat.title}</h3>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{cat.desc}</p>
                    <p className="mt-1.5 text-[11px] font-semibold gold-text">
                      {(cat.articles || []).length} {t('docs.page.articles_label')}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          <ContactCta t={t} waEnabled={waEnabled} />
        </div>
      )}
    </div>
  );
}
